/**
 * Remux a raw AAC ADTS stream into an MP4 (M4A) container.
 *
 * The Android voice-recorder plugin produces AAC frames wrapped in ADTS
 * headers (one 7-byte header per frame). Some consumers (e.g. OpenAI Whisper)
 * require a real MP4 container. This function strips the ADTS headers,
 * keeps the raw AAC payloads, and writes a minimal ISO-BMFF (MP4) file.
 *
 * No re-encoding happens — only the container is changed.
 *
 * Spec references: ISO/IEC 14496-12 (ISO-BMFF), 14496-14 (MP4),
 * 14496-3 (AAC AudioSpecificConfig), ISO/IEC 13818-7 (ADTS).
 */

const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

interface AdtsFrame {
  data: Uint8Array;
}

interface AdtsInfo {
  profile: number;
  sampleRateIndex: number;
  sampleRate: number;
  channelConfig: number;
  frames: AdtsFrame[];
}

function parseAdts(bytes: Uint8Array): AdtsInfo {
  const frames: AdtsFrame[] = [];
  let offset = 0;
  let profile = 0;
  let sampleRateIndex = 0;
  let channelConfig = 0;

  while (offset + 7 <= bytes.length) {
    if (bytes[offset] !== 0xff || (bytes[offset + 1] & 0xf0) !== 0xf0) {
      throw new Error(`Invalid ADTS sync at byte ${offset}`);
    }

    const protectionAbsent = bytes[offset + 1] & 0x01;
    profile = (bytes[offset + 2] >> 6) & 0x03;
    sampleRateIndex = (bytes[offset + 2] >> 2) & 0x0f;
    channelConfig = ((bytes[offset + 2] & 0x01) << 2) | ((bytes[offset + 3] >> 6) & 0x03);
    const frameLength =
      ((bytes[offset + 3] & 0x03) << 11) | (bytes[offset + 4] << 3) | ((bytes[offset + 5] >> 5) & 0x07);

    const headerLength = protectionAbsent ? 7 : 9;
    if (frameLength < headerLength || offset + frameLength > bytes.length) {
      throw new Error(`Truncated ADTS frame at byte ${offset}`);
    }

    frames.push({
      data: bytes.subarray(offset + headerLength, offset + frameLength),
    });
    offset += frameLength;
  }

  if (frames.length === 0) throw new Error('No ADTS frames found');
  if (sampleRateIndex >= SAMPLE_RATES.length) {
    throw new Error(`Unsupported sample rate index ${sampleRateIndex}`);
  }

  return {
    profile,
    sampleRateIndex,
    sampleRate: SAMPLE_RATES[sampleRateIndex],
    channelConfig,
    frames,
  };
}

class BoxWriter {
  private chunks: Uint8Array[] = [];
  private length = 0;

  bytes(data: Uint8Array | number[]): void {
    const arr = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.chunks.push(arr);
    this.length += arr.byteLength;
  }

  u8(v: number): void {
    this.bytes([v & 0xff]);
  }

  u16(v: number): void {
    this.bytes([(v >> 8) & 0xff, v & 0xff]);
  }

  u24(v: number): void {
    this.bytes([(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff]);
  }

  u32(v: number): void {
    this.bytes([(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff]);
  }

  ascii(s: string): void {
    const arr = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) arr[i] = s.charCodeAt(i) & 0xff;
    this.bytes(arr);
  }

  toUint8Array(): Uint8Array {
    const out = new Uint8Array(this.length);
    let pos = 0;
    for (const c of this.chunks) {
      out.set(c, pos);
      pos += c.byteLength;
    }
    return out;
  }
}

function box(type: string, ...payload: Uint8Array[]): Uint8Array {
  let size = 8;
  for (const p of payload) size += p.byteLength;
  const w = new BoxWriter();
  w.u32(size);
  w.ascii(type);
  for (const p of payload) w.bytes(p);
  return w.toUint8Array();
}

function fullBox(type: string, version: number, flags: number, ...payload: Uint8Array[]): Uint8Array {
  const head = new BoxWriter();
  head.u8(version);
  head.u24(flags);
  return box(type, head.toUint8Array(), ...payload);
}

function audioSpecificConfig(profile: number, sampleRateIndex: number, channelConfig: number): Uint8Array {
  // AudioObjectType = ADTS profile + 1 (LC=2). Bits: AOT(5) | SFI(4) | CC(4) | 3 zero bits.
  const aot = profile + 1;
  const b0 = ((aot & 0x1f) << 3) | ((sampleRateIndex >> 1) & 0x07);
  const b1 = ((sampleRateIndex & 0x01) << 7) | ((channelConfig & 0x0f) << 3);
  return new Uint8Array([b0, b1]);
}

function esdsBox(asc: Uint8Array, avgBitrate: number): Uint8Array {
  // ES_Descriptor (tag 0x03) > DecoderConfigDescriptor (tag 0x04) >
  // DecoderSpecificInfo (tag 0x05) + SLConfigDescriptor (tag 0x06).
  const dsi = new BoxWriter();
  dsi.u8(0x05);
  dsi.u8(asc.byteLength);
  dsi.bytes(asc);

  const dcd = new BoxWriter();
  dcd.u8(0x04);
  dcd.u8(13 + 2 + asc.byteLength);
  dcd.u8(0x40); // objectTypeIndication: MPEG-4 Audio
  dcd.u8(0x15); // streamType (Audio=5) << 2 | upstream=0 | reserved=1
  dcd.u24(0); // bufferSizeDB
  dcd.u32(avgBitrate); // maxBitrate
  dcd.u32(avgBitrate); // avgBitrate
  dcd.bytes(dsi.toUint8Array());

  const sl = new BoxWriter();
  sl.u8(0x06);
  sl.u8(1);
  sl.u8(0x02);

  const es = new BoxWriter();
  es.u8(0x03);
  es.u8(3 + dcd.toUint8Array().byteLength + sl.toUint8Array().byteLength);
  es.u16(0); // ES_ID
  es.u8(0); // flags
  es.bytes(dcd.toUint8Array());
  es.bytes(sl.toUint8Array());

  return fullBox('esds', 0, 0, es.toUint8Array());
}

function mp4aBox(channelConfig: number, sampleRate: number, esds: Uint8Array): Uint8Array {
  const w = new BoxWriter();
  w.bytes(new Uint8Array(6)); // reserved
  w.u16(1); // data_reference_index
  w.u32(0);
  w.u32(0); // version, revision, vendor (audio reserved)
  w.u16(channelConfig);
  w.u16(16); // sample size
  w.u16(0);
  w.u16(0); // pre_defined, reserved
  // sample rate is stored as 16.16 fixed point; only top 16 bits are read
  w.u16(sampleRate);
  w.u16(0);
  w.bytes(esds);
  return box('mp4a', w.toUint8Array());
}

function stsdBox(mp4a: Uint8Array): Uint8Array {
  const w = new BoxWriter();
  w.u32(1); // entry_count
  w.bytes(mp4a);
  return fullBox('stsd', 0, 0, w.toUint8Array());
}

function sttsBox(frameCount: number, samplesPerFrame: number): Uint8Array {
  const w = new BoxWriter();
  w.u32(1);
  w.u32(frameCount);
  w.u32(samplesPerFrame);
  return fullBox('stts', 0, 0, w.toUint8Array());
}

function stscBox(frameCount: number): Uint8Array {
  const w = new BoxWriter();
  w.u32(1);
  w.u32(1); // first_chunk
  w.u32(frameCount); // samples_per_chunk
  w.u32(1); // sample_description_index
  return fullBox('stsc', 0, 0, w.toUint8Array());
}

function stszBox(sizes: number[]): Uint8Array {
  const w = new BoxWriter();
  w.u32(0); // sample_size (0 = variable, listed below)
  w.u32(sizes.length);
  for (const s of sizes) w.u32(s);
  return fullBox('stsz', 0, 0, w.toUint8Array());
}

function stcoBox(mdatPayloadOffset: number): Uint8Array {
  const w = new BoxWriter();
  w.u32(1);
  w.u32(mdatPayloadOffset);
  return fullBox('stco', 0, 0, w.toUint8Array());
}

function buildMoov(info: AdtsInfo, mdatPayloadOffset: number): Uint8Array {
  const samplesPerFrame = 1024; // AAC LC fixed
  const totalSamples = info.frames.length * samplesPerFrame;
  // 16.16 fixed-point timescale would be sampleRate; using sampleRate as
  // timescale keeps duration math integer and exact.
  const timescale = info.sampleRate;
  const duration = totalSamples;

  const asc = audioSpecificConfig(info.profile, info.sampleRateIndex, info.channelConfig);
  const totalPayload = info.frames.reduce((n, f) => n + f.data.byteLength, 0);
  const avgBitrate = Math.round((totalPayload * 8 * info.sampleRate) / totalSamples);
  const esds = esdsBox(asc, avgBitrate);
  const mp4a = mp4aBox(info.channelConfig, info.sampleRate, esds);

  const mvhd = (() => {
    const w = new BoxWriter();
    w.u32(0);
    w.u32(0); // creation/modification time
    w.u32(timescale);
    w.u32(duration);
    w.u32(0x00010000); // rate 1.0
    w.u16(0x0100); // volume 1.0
    w.u16(0); // reserved
    w.u32(0);
    w.u32(0); // reserved
    // unity matrix
    w.u32(0x00010000);
    w.u32(0);
    w.u32(0);
    w.u32(0);
    w.u32(0x00010000);
    w.u32(0);
    w.u32(0);
    w.u32(0);
    w.u32(0x40000000);
    for (let i = 0; i < 6; i++) w.u32(0); // pre_defined
    w.u32(2); // next_track_ID
    return fullBox('mvhd', 0, 0, w.toUint8Array());
  })();

  const tkhd = (() => {
    const w = new BoxWriter();
    w.u32(0);
    w.u32(0); // creation/modification time
    w.u32(1); // track_ID
    w.u32(0); // reserved
    w.u32(duration);
    w.u32(0);
    w.u32(0); // reserved
    w.u16(0); // layer
    w.u16(0); // alternate_group
    w.u16(0x0100); // volume
    w.u16(0); // reserved
    w.u32(0x00010000);
    w.u32(0);
    w.u32(0);
    w.u32(0);
    w.u32(0x00010000);
    w.u32(0);
    w.u32(0);
    w.u32(0);
    w.u32(0x40000000);
    w.u32(0); // width
    w.u32(0); // height
    return fullBox('tkhd', 0, 0x000007, w.toUint8Array());
  })();

  const mdhd = (() => {
    const w = new BoxWriter();
    w.u32(0);
    w.u32(0);
    w.u32(timescale);
    w.u32(duration);
    w.u16(0x55c4); // language "und"
    w.u16(0);
    return fullBox('mdhd', 0, 0, w.toUint8Array());
  })();

  const hdlr = (() => {
    const w = new BoxWriter();
    w.u32(0);
    w.ascii('soun');
    w.u32(0);
    w.u32(0);
    w.u32(0);
    w.ascii('SoundHandler\0');
    return fullBox('hdlr', 0, 0, w.toUint8Array());
  })();

  const smhd = (() => {
    const w = new BoxWriter();
    w.u16(0); // balance
    w.u16(0); // reserved
    return fullBox('smhd', 0, 0, w.toUint8Array());
  })();

  const dref = (() => {
    const url = fullBox('url ', 0, 1);
    const w = new BoxWriter();
    w.u32(1);
    w.bytes(url);
    return fullBox('dref', 0, 0, w.toUint8Array());
  })();
  const dinf = box('dinf', dref);

  const sizes = info.frames.map((f) => f.data.byteLength);
  const stbl = box(
    'stbl',
    stsdBox(mp4a),
    sttsBox(info.frames.length, samplesPerFrame),
    stscBox(info.frames.length),
    stszBox(sizes),
    stcoBox(mdatPayloadOffset),
  );

  const minf = box('minf', smhd, dinf, stbl);
  const mdia = box('mdia', mdhd, hdlr, minf);
  const trak = box('trak', tkhd, mdia);
  return box('moov', mvhd, trak);
}

function concat(parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.byteLength;
  const out = new Uint8Array(len);
  let pos = 0;
  for (const p of parts) {
    out.set(p, pos);
    pos += p.byteLength;
  }
  return out;
}

/** Remuxes an AAC ADTS byte stream into an MP4 (M4A) byte stream. */
export function aacAdtsToMp4(adts: Uint8Array): Uint8Array {
  const info = parseAdts(adts);

  const ftyp = box(
    'ftyp',
    new Uint8Array([
      0x4d,
      0x34,
      0x41,
      0x20, // 'M4A '
      0x00,
      0x00,
      0x00,
      0x00, // minor version
      0x4d,
      0x34,
      0x41,
      0x20, // 'M4A '
      0x6d,
      0x70,
      0x34,
      0x32, // 'mp42'
      0x69,
      0x73,
      0x6f,
      0x6d, // 'isom'
    ]),
  );

  // First pass with a placeholder offset to size moov, then rebuild with the
  // real mdat payload offset (= ftyp + moov + 8-byte mdat header).
  const moovProbe = buildMoov(info, 0);
  const mdatPayloadOffset = ftyp.byteLength + moovProbe.byteLength + 8;
  const moov = buildMoov(info, mdatPayloadOffset);

  const mdatPayload = concat(info.frames.map((f) => f.data));
  const mdat = (() => {
    const w = new BoxWriter();
    w.u32(8 + mdatPayload.byteLength);
    w.ascii('mdat');
    w.bytes(mdatPayload);
    return w.toUint8Array();
  })();

  return concat([ftyp, moov, mdat]);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
  }
  return btoa(bin);
}

/** Convenience wrapper for the base64 payloads returned by Capacitor plugins. */
export function aacAdtsToMp4Base64(base64Adts: string): string {
  return bytesToBase64(aacAdtsToMp4(base64ToBytes(base64Adts)));
}
